import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import GuideMeta from "./GuideMeta";

/**
 * GuideCard - the ONE modular guide card for /blog. Used in the index grid and
 * in the "Related guides" section on an article page (one component, two call
 * sites).
 *
 * Design (approved concept):
 *  - Flat, warm-dark: Pit Black surface on the charcoal page, one hairline
 *    border, tonal recession, no floating blur (Flat-By-Default).
 *  - Real article text only: `title` and `description` render verbatim from the
 *    API (SEO + live tests depend on the exact strings). No paraphrase, no
 *    year-stripping. No emoji.
 *  - Meta uses the shared GuideMeta row (gold category | N min read | Updated
 *    <date>), so the card is visibly one system with the hero and post header.
 *  - One Lamp: the only standing gold is the category text; the hover accent
 *    (title + arrow) is transient (200-300ms, brand easing, via .blog-card).
 *  - The whole card is a single Link to the guide, with a focus-visible ring.
 */
const GuideCard = ({ post, className = "" }) => {
  if (!post) return null;

  const {
    slug,
    title,
    description,
    category,
    updated,
    date,
    reading_time: readingTime,
  } = post;

  return (
    <Link
      to={`/blog/${slug}`}
      className={`blog-card group flex h-full flex-col rounded-xl border border-border bg-card p-6 outline-none hover:-translate-y-1 hover:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring ${className}`}
    >
      {/* Topic: the primary read (real title verbatim, year suffix included) */}
      <h3 className="text-balance text-lg font-bold leading-snug tracking-tight text-white transition-colors duration-200 line-clamp-3 group-hover:text-primary">
        {title}
      </h3>

      {/* Real description verbatim, clamped */}
      {description ? (
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}

      {/* Meta row (shared) plus the quiet directional affordance */}
      <div className="mt-auto flex items-end justify-between gap-3 pt-6">
        <GuideMeta
          category={category}
          readingTime={readingTime}
          updated={updated || date}
        />
        <ArrowRight
          aria-hidden="true"
          className="mb-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-all duration-200 motion-safe:group-hover:translate-x-0.5 group-hover:text-primary"
        />
      </div>
    </Link>
  );
};

/** Skeleton matching GuideCard's footprint so the grid does not reflow. */
export const GuideCardSkeleton = () => (
  <div className="flex h-full flex-col rounded-xl border border-border bg-card p-6">
    <div className="h-5 w-3/4 animate-pulse rounded bg-secondary" />
    <div className="mt-3 h-4 w-full animate-pulse rounded bg-secondary" />
    <div className="mt-2 h-4 w-2/3 animate-pulse rounded bg-secondary" />
    <div className="mt-auto flex items-center gap-2 pt-6">
      <div className="h-3 w-14 animate-pulse rounded bg-secondary" />
      <div className="h-3 w-16 animate-pulse rounded bg-secondary" />
      <div className="h-3 w-24 animate-pulse rounded bg-secondary" />
    </div>
  </div>
);

export default GuideCard;
